'use client';

/**
 * Carousel — Swiper 기반 캐러셀 컴포넌트
 *
 * @example
 * ```tsx
 * import { Navigation, Pagination } from 'swiper';
 *
 * <Carousel modules={[Navigation, Pagination]} gap={24} maxWidth={{ pc: '396px', mo: '320px' }}>
 *   {items.map((item) => <CardItem key={item.id} {...item} />)}
 * </Carousel>
 * ```
 *
 * [사용 패턴]
 * 1. 슬라이드만 — `modules` 생략. 스와이프로만 넘긴다
 * 2. 기본 화살표 — `modules={[Navigation]}`. 슬라이더 양옆 세로 중앙, PC만 노출
 * 3. 페이지네이션 — `modules={[Pagination]}` + `paginationType`. 슬라이더 아래 고정
 * 4. 화살표 + 페이지네이션 — `modules={[Navigation, Pagination]}`
 * 5. 기본 위치에서 버튼 모양만 변경 — `prevButton` / `nextButton`
 * 6. 화살표를 다른 위치에 — CarouselProvider + CarouselPrev/Next (아래 CarouselProvider 주석 참고)
 * 7. 자동재생 — `modules={[Autoplay]}` + `autoplay={{ delay }}`. 화면에 보일 때만 재생된다
 * 8. 외부 탭 등과 연동 — `onSwiper`로 받은 swiper 인스턴스로 `slideTo` / `slideToLoop` 호출
 *
 * [주의]
 * - children은 슬라이드 내용만 넘긴다 — 래퍼가 각 child를 SwiperSlide로 감싸므로 `<SwiperSlide>`를 넣으면 2중이 되고,
 *   Fragment(`<>{a}{b}</>`)는 슬라이드 1장으로 묶인다.
 * - `autoplay`를 쓰면 `modules`에 `Autoplay`도 넣어야 한다 — 빠지면 에러 없이 자동재생만 동작하지 않는다.
 * - `maxWidth={{ mo }}`만 주면 PC에도 같은 값이 걸린다 (`pc ?? mo` fallback). PC 제한이 없어야 하면 `pc: 'none'`.
 * - `Navigation`을 넣으면 PC에서 화살표 자리만큼 wrapper가 좌우로 확장된다 (Carousel.module.scss `&_wrapper`).
 * - 슬라이더 모서리 radius 등 시안별 스타일은 `className`(Swiper 루트)으로 지정한다.
 * - `style` · `navigation` · `pagination` · `spaceBetween` · `resistanceRatio` · `loopedSlides`는 래퍼가 덮어쓴다 — 간격은 `gap`으로.
 * - `modules` · `onSwiper` · 함수형 `prevButton` · 함수형 CarouselPrev children 등 함수 props는 서버 컴포넌트에서 넘길 수 없다 — 호출 파일에 'use client' 필요.
 * - `breakpoints` 키는 `1024.02`(SCSS `$bp-pc`)를 쓴다 — 1024·1025를 쓰면 경계 폭에서 CSS와 Swiper 레이아웃이 어긋난다.
 */

import clsx from 'clsx';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide, type SwiperProps } from 'swiper/react';
import { Navigation, Pagination } from 'swiper';
import type { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import {
  Children,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Button } from '@publishing/components/ui/Button';
import Icon from '@publishing/components/ui/Icon';
import { ArrowChevron } from '@publishing/components/ui/SvgSprite';
import {
  CarouselContext,
  CarouselNavContext,
  useCarousel,
  useCarouselNav,
} from './useCarousel';
import s from './Carousel.module.scss';

// ── type ──
type NavigationTheme = 'black' | 'white';

/** 화살표 버튼 노드. 함수로 넘기면 `disabled`(Prev는 첫 슬라이드, Next는 마지막 슬라이드에서 true. loop이면 항상 false)를 받는다 */
type NavButtonContent = ReactNode | ((disabled: boolean) => ReactNode);

/** 그 외 Swiper 옵션 그대로 전달 (loop · autoplay · speed · slidesPerView(기본 'auto') · breakpoints 등) */
type PassThroughSwiperProps = Omit<
  SwiperProps,
  | 'navigation'
  | 'pagination'
  | 'spaceBetween'
  | 'children'
  | 'onSwiper'
  | 'onSlideChange'
  | 'onResize'
  | 'onReachBeginning'
  | 'onReachEnd'
  | 'onFromEdge'
>;

type CarouselSwiperProps = PassThroughSwiperProps & {
  children: ReactNode;
  className?: string;
  /** 슬라이드 간격(px). 기본 20 */
  gap?: number;
  /** 슬라이드 최대 너비 — string이면 PC/MO 동일, 객체면 { pc, mo } */
  maxWidth?: string | { pc?: string; mo?: string };
  /** 사용할 Swiper 모듈 (Navigation · Pagination · Autoplay · EffectFade 등). 호출부에서 import해 넘긴다 (트리쉐이킹) */
  modules?: SwiperModule[];
  /** 기본 'bullets' */
  paginationType?: 'bullets' | 'fraction' | 'progressbar';
  /** swiper 인스턴스 — 외부 탭 연동 등 직접 제어가 필요할 때 */
  onSwiper?: (swiper: SwiperClass) => void;
  onSlideChange?: (swiper: SwiperClass) => void;
  onResize?: (swiper: SwiperClass) => void;
};

type CarouselProps = CarouselSwiperProps & {
  /** 기본 화살표 wrapper className. CarouselProvider로 감싸면 쓰이지 않는다 */
  navigationClassName?: string;
  /** 기본 화살표·페이지네이션 색. 기본 'black' */
  navigationTheme?: NavigationTheme;
  /** 페이지네이션 wrapper className */
  paginationClassName?: string;
  /** 기본 위치에서 버튼 모양만 바꿀 때. 넘기면 `hidden_mo`가 빠지므로 모바일 노출은 호출부 CSS로 정한다 */
  prevButton?: NavButtonContent;
  nextButton?: NavButtonContent;
};

type CarouselProviderProps = {
  children: ReactNode;
};

type CarouselNavButtonProps = {
  /** 생략하면 기본 40px 화살표 버튼 */
  children?: NavButtonContent;
  className?: string;
  /** 기본 버튼 색. 기본 'black' */
  navigationTheme?: NavigationTheme;
};

type CarouselNavigationProps = {
  children: ReactNode;
  className?: string;
  navigationTheme?: NavigationTheme;
  prevButton?: NavButtonContent;
  nextButton?: NavButtonContent;
};

type CarouselPaginationProps = {
  className?: string;
  navigationTheme?: NavigationTheme;
};

// ── const ──
const renderBullet = (index: number, bulletClass: string) =>
  `<button type="button" class="${bulletClass}" aria-label="${index + 1}번 슬라이드로 이동"></button>`;

const renderFraction = (currentClass: string, totalClass: string) =>
  `<span class="${currentClass}"></span> | <span class="${totalClass}"></span>`;

// ── function ──
export function Carousel({
  navigationClassName,
  navigationTheme,
  paginationClassName,
  prevButton,
  nextButton,
  modules = [],
  ...rest
}: CarouselProps) {
  const hasNavigation = modules.includes(Navigation);
  const hasPagination = modules.includes(Pagination);
  const hasExternalProvider = useContext(CarouselContext) !== null;

  const swiperWithPagination = (
    <>
      <CarouselSwiper {...rest} modules={modules} />
      {hasPagination && (
        <CarouselPagination
          className={paginationClassName}
          navigationTheme={navigationTheme}
        />
      )}
    </>
  );

  const withNavigation =
    hasNavigation && !hasExternalProvider ? (
      <CarouselNavigation
        className={navigationClassName}
        navigationTheme={navigationTheme}
        prevButton={prevButton}
        nextButton={nextButton}
      >
        {swiperWithPagination}
      </CarouselNavigation>
    ) : (
      swiperWithPagination
    );

  return hasExternalProvider ? withNavigation : <CarouselProvider>{withNavigation}</CarouselProvider>;
}

/**
 * 화살표를 슬라이더 양옆이 아닌 곳(제목 옆, 하단 등)에 둘 때 Carousel과 CarouselPrev/Next를 함께 감싼다.
 * - Provider 하나에 Carousel 하나만 둔다 — 둘 이상이면 화살표·페이지네이션이 마지막 Carousel에만 연결된다
 * - `modules`에 `Navigation`은 그대로 넣는다
 * - CarouselPrev/Next는 슬라이드 안에 둘 수 없다
 * - 바깥에 둔 화살표는 `hidden_mo`가 붙지 않는다 — 모바일 노출은 호출부 CSS로 정한다
 *
 * @example
 * ```tsx
 * <CarouselProvider>
 *   <div className={s.head}>
 *     <h2>제목</h2>
 *     <CarouselPrev className={s.prev} />
 *     <CarouselNext className={s.next} navigationTheme="white" />
 *   </div>
 *   <Carousel modules={[Navigation]}>{slides}</Carousel>
 * </CarouselProvider>
 * ```
 */
export function CarouselProvider({ children }: CarouselProviderProps) {
  const [prevEl, setPrevEl] = useState<HTMLDivElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLDivElement | null>(null);
  const [paginationEl, setPaginationEl] = useState<HTMLDivElement | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // DOM ref/setter 포함 — 슬라이드 이동 시 변경되지 않으므로 Carousel 불필요 리렌더 방지
  const staticValue = useMemo(
    () => ({
      prevEl,
      nextEl,
      paginationEl,
      setPrevEl,
      setNextEl,
      setPaginationEl,
      setIsBeginning,
      setIsEnd,
    }),
    [prevEl, nextEl, paginationEl],
  );

  // nav 상태만 별도 context — CarouselPrev/Next만 구독
  const navValue = useMemo(
    () => ({ isBeginning, isEnd }),
    [isBeginning, isEnd],
  );

  return (
    <CarouselContext.Provider value={staticValue}>
      <CarouselNavContext.Provider value={navValue}>
        {children}
      </CarouselNavContext.Provider>
    </CarouselContext.Provider>
  );
}

function CarouselSwiper({
  children,
  className,
  gap = 20,
  slidesPerView = 'auto',
  maxWidth,
  paginationType = 'bullets',
  autoplay,
  onSwiper,
  onSlideChange,
  onResize,
  ...rest
}: CarouselSwiperProps) {
  const { prevEl, nextEl, paginationEl, setIsBeginning, setIsEnd } =
    useCarousel();
  const resolvedMaxWidth = typeof maxWidth === 'string' ? { pc: maxWidth, mo: maxWidth } : maxWidth;
  const slides = Children.toArray(children);
  const slideStyle =
    resolvedMaxWidth &&
    ({
      '--slide-max-width-pc': resolvedMaxWidth.pc ?? resolvedMaxWidth.mo,
      '--slide-max-width-mo': resolvedMaxWidth.mo ?? resolvedMaxWidth.pc,
    } as CSSProperties);

  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => () => observerRef.current?.disconnect(), []);

  const updateSlidesEdge = useCallback(
    (swiper: SwiperClass) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
    [setIsBeginning, setIsEnd],
  );

  const handleSwiper = useCallback(
    (swiper: SwiperClass) => {
      updateSlidesEdge(swiper);
      onSwiper?.(swiper);
      if (!autoplay) return;
      if (!swiper.autoplay) return;

      observerRef.current?.disconnect();
      swiper.autoplay.stop();
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) swiper.autoplay.start();
          else swiper.autoplay.stop();
        },
        { threshold: 0 },
      );
      observerRef.current.observe(swiper.el);
    },
    [updateSlidesEdge, onSwiper, autoplay],
  );

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      updateSlidesEdge(swiper);
      onSlideChange?.(swiper);
    },
    [updateSlidesEdge, onSlideChange],
  );

  const handleResize = useCallback(
    (swiper: SwiperClass) => {
      updateSlidesEdge(swiper);
      onResize?.(swiper);
    },
    [updateSlidesEdge, onResize],
  );

  return (
    <Swiper
      {...rest}
      resistanceRatio={0}
      autoplay={autoplay}
      loopedSlides={rest.loop ? slides.length : undefined}
      roundLengths={rest.roundLengths ?? true}
      slidesPerView={slidesPerView}
      spaceBetween={gap}
      style={slideStyle || undefined}
      className={clsx(s.carousel, className)}
      navigation={prevEl && nextEl ? { prevEl, nextEl } : false}
      pagination={
        paginationEl
          ? {
              el: paginationEl,
              type: paginationType,
              clickable: paginationType !== 'fraction',
              renderBullet,
              ...(paginationType === 'fraction' && { renderFraction }),
            }
          : false
      }
      onSwiper={handleSwiper}
      onSlideChange={handleSlideChange}
      onResize={handleResize}
      onReachBeginning={updateSlidesEdge}
      onReachEnd={updateSlidesEdge}
      onFromEdge={updateSlidesEdge}
    >
      {slides.map((child, index) => (
        <SwiperSlide key={`carousel-slide-${index}`} className={s.carousel_slide}>
          <CarouselContext.Provider value={null}>{child}</CarouselContext.Provider>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

export function CarouselPrev(props: CarouselNavButtonProps) {
  return <CarouselNavButton direction="prev" {...props} />;
}

export function CarouselNext(props: CarouselNavButtonProps) {
  return <CarouselNavButton direction="next" {...props} />;
}

function CarouselNavButton({
  direction,
  children,
  className,
  navigationTheme,
}: CarouselNavButtonProps & { direction: 'prev' | 'next' }) {
  const { setPrevEl, setNextEl } = useCarousel();
  const { isBeginning, isEnd } = useCarouselNav();
  const isPrev = direction === 'prev';
  const disabled = isPrev ? isBeginning : isEnd;
  const content =
    children ??
    ((isDisabled: boolean) => (
      <Button
        theme="icon"
        type={navigationTheme}
        aria-label={isPrev ? '이전 슬라이드' : '다음 슬라이드'}
        disabled={isDisabled}
      >
        <Icon name={ArrowChevron} direction={isPrev ? 'left' : 'right'} size={40} />
      </Button>
    ));

  return (
    <div ref={isPrev ? setPrevEl : setNextEl} className={className}>
      {typeof content === 'function' ? content(disabled) : content}
    </div>
  );
}

function CarouselNavigation({
  children,
  className,
  navigationTheme,
  prevButton,
  nextButton,
}: CarouselNavigationProps) {
  return (
    <div className={clsx(s.carousel_wrapper, className)}>
      <CarouselPrev
        className={clsx(s.carousel_nav_prev, !prevButton && 'hidden_mo')}
        navigationTheme={navigationTheme}
      >
        {prevButton}
      </CarouselPrev>
      {children}
      <CarouselNext
        className={clsx(s.carousel_nav_next, !nextButton && 'hidden_mo')}
        navigationTheme={navigationTheme}
      >
        {nextButton}
      </CarouselNext>
    </div>
  );
}

function CarouselPagination({ className, navigationTheme }: CarouselPaginationProps) {
  const { setPaginationEl } = useCarousel();
  const themeClass =
    navigationTheme === 'white' ? s.carousel_pagination_white : '';

  return (
    <div
      ref={setPaginationEl}
      className={clsx(s.carousel_pagination, themeClass, className)}
    />
  );
}
